import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Handshake, Copy, WhatsappLogo, QrCode, CurrencyDollar, TrendUp, Storefront,
  Users, ArrowRight, Trash,
} from "@phosphor-icons/react";
import { api, brl, getUser } from "@/lib/api";
import { publicUrl } from "@/lib/publicUrl";
import Paginator from "@/components/Paginator";

const buildRefLink = (slug, code) =>
  publicUrl(`/f/${slug}${code ? `?ref=${code}` : ""}`);

const StatBox = ({ label, value, Icon, color = "var(--accent)", testid }) => (
  <div className="card-flat p-6" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="label-caps">{label}</div>
        <div className="font-display font-black text-3xl mt-2" style={{ color }}>{value}</div>
      </div>
      <Icon size={28} color={color} weight="duotone" />
    </div>
  </div>
);

const FunnelRow = ({ f, code, isAdmin, onDelete }) => {
  const [showQr, setShowQr] = useState(false);
  const link = buildRefLink(f.slug, code);
  const wapp = `https://wa.me/?text=${encodeURIComponent(`Confere isso: ${link}`)}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(link)}`;
  const price = f.plans?.[0]?.price || 0;
  const rate = f.affiliate_rate || 0.1;
  const perSale = price * rate;

  return (
    <div className="card-flat p-5 relative" data-testid={`market-funnel-${f.id}`}>
      {isAdmin && (
        <button
          onClick={() => onDelete(f)}
          data-testid={`market-delete-${f.id}`}
          title="Excluir funil (admin)"
          className="absolute top-3 right-3 p-2 rounded-sm border border-[var(--danger)]/40 text-[var(--danger)] hover:bg-[var(--danger)]/10 transition"
        >
          <Trash size={14} />
        </button>
      )}
      <div className="flex justify-between items-start gap-3 pr-8">
        <div className="min-w-0">
          <div className="font-display font-bold text-lg truncate">{f.title}</div>
          <div className="text-xs text-white/50 mt-1">Por {f.owner_name || "produtor"}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="label-caps text-[10px]">Comissão</div>
          <div className="text-[var(--accent)] font-bold text-lg">{(rate * 100).toFixed(0)}%</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 text-sm">
        <div className="text-white/70">A partir de <b className="text-white">{brl(price)}</b></div>
        <div className="text-white/30">•</div>
        <div className="text-[var(--success)]">Ganhe {brl(perSale)}/venda</div>
      </div>
      <div className="mt-3 font-mono text-[11px] break-all text-white/60 bg-black/30 p-2 rounded-sm" data-testid={`market-link-${f.id}`}>
        {link}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={() => {
            navigator.clipboard.writeText(link);
            toast.success("Link copiado com seu código de afiliado");
          }}
          className="btn-ghost text-xs flex items-center gap-1"
          data-testid={`market-copy-${f.id}`}
        >
          <Copy size={14} /> Copiar
        </button>
        <a
          href={wapp}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost text-xs flex items-center gap-1"
          data-testid={`market-wapp-${f.id}`}
        >
          <WhatsappLogo size={14} /> WhatsApp
        </a>
        <button
          onClick={() => setShowQr((v) => !v)}
          className="btn-ghost text-xs flex items-center gap-1"
          data-testid={`market-qr-${f.id}`}
        >
          <QrCode size={14} /> QR
        </button>
        <a
          href={`/f/${f.slug}`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost text-xs flex items-center gap-1 ml-auto text-[var(--accent)]"
          data-testid={`market-view-${f.id}`}
        >
          Ver funil <ArrowRight size={12} />
        </a>
      </div>
      {showQr && (
        <div className="mt-3 flex justify-center bg-white p-3 rounded-sm">
          <img src={qrSrc} alt="QR" width={180} height={180} />
        </div>
      )}
    </div>
  );
};

export default function Affiliate() {
  const [summary, setSummary] = useState({
    affiliate_code: "", total_earned: 0, sales_count: 0, gross_volume: 0, top_funnels: [],
  });
  const [market, setMarket] = useState({ funnels: [], page: 1, pages: 1, total: 0 });
  const [sales, setSales] = useState({ sales: [], page: 1, pages: 1, total: 0 });
  const user = getUser();
  const isAdmin = user?.role === "admin";

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/account/affiliate");
      setSummary(data);
    } catch (e) { console.warn("summary failed", e?.message); }
  };
  const loadMarket = async (p = 1) => {
    try {
      const { data } = await api.get(`/public/funnels?page=${p}&per_page=9`);
      setMarket(data);
    } catch (e) { console.warn("market failed", e?.message); }
  };
  const loadSales = async (p = 1) => {
    try {
      const { data } = await api.get(`/account/affiliate/sales?page=${p}&per_page=10`);
      setSales(data);
    } catch (e) { console.warn("sales failed", e?.message); }
  };

  const deleteFunnel = async (f) => {
    const ok = window.confirm(
      `⚠️ ADMIN: Excluir permanentemente o funil "${f.title}"?\n\nO funil ficará inacessível para todos (produtor e afiliados).`,
    );
    if (!ok) return;
    try {
      await api.delete(`/funnels/${f.id}`);
      toast.success(`Funil "${f.title}" removido do marketplace`);
      loadMarket(market.page);
    } catch (e) { toast.error("Erro ao remover: " + (e?.response?.data?.detail || e?.message)); }
  };

  useEffect(() => {
    loadSummary();
    loadMarket(1);
    loadSales(1);
  }, []);

  const code = summary.affiliate_code;
  const [myLink, setMyLink] = useState({ personal_slug: "", full_url: "" });
  useEffect(() => {
    api.get("/me/personal-link")
      .then(({ data }) => setMyLink(data))
      .catch(() => {});
  }, []);

  // Monta a URL personalizada sempre com o domínio oficial mercadopagu.com.br,
  // ignorando o full_url do backend (que pode vir com domínio de preview/deploy antigo
  // caso o FRONTEND_ORIGIN do backend ainda não tenha sido atualizado em produção).
  const myPersonalUrl = myLink.personal_slug ? publicUrl(`/${myLink.personal_slug}`) : "";

  return (
    <div className="fadeUp">
      <div className="flex justify-between items-baseline mb-10">
        <div>
          <div className="label-caps mb-2">Programa de afiliação</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Afiliado MercadoPagu</h1>
          <p className="text-white/60 mt-2">
            Promova qualquer funil da plataforma. Ganhe até 10% em cada venda direto no seu saldo.
          </p>
        </div>
        <div className="text-right">
          <div className="label-caps">Seu código</div>
          <div className="font-mono text-3xl font-bold text-[var(--accent)]" data-testid="affiliate-code">
            {code || "..."}
          </div>
          {code && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(code);
                toast.success("Código copiado");
              }}
              className="mt-2 text-xs text-white/60 hover:text-white flex items-center gap-1 ml-auto"
              data-testid="copy-affiliate-code"
            >
              <Copy size={12} /> Copiar código
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <StatBox label="Ganho total" value={brl(summary.total_earned)}
                 Icon={CurrencyDollar} color="var(--success)" testid="stat-earned" />
        <StatBox label="Vendas confirmadas" value={summary.sales_count}
                 Icon={TrendUp} testid="stat-sales" />
        <StatBox label="Volume movimentado" value={brl(summary.gross_volume)}
                 Icon={Handshake} testid="stat-volume" />
      </div>

      {myPersonalUrl && (
        <div className="card-flat p-6 mb-6 border-l-2 border-l-[var(--accent)]" data-testid="my-personal-link">
          <div className="label-caps mb-2">🔗 Seu link personalizado</div>
          <div className="font-display font-bold text-xl mb-3">Compartilhe seu link curto</div>
          <div className="bg-black/40 border border-[var(--border)] rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap">
            <code className="font-mono text-sm text-[var(--accent)] break-all flex-1" data-testid="my-personal-url">{myPersonalUrl}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(myPersonalUrl); toast.success("Link copiado!"); }}
              className="px-3 py-1.5 text-xs border border-[var(--accent)] text-[var(--accent)] rounded-sm hover:bg-[var(--accent)]/10 flex items-center gap-1"
              data-testid="copy-my-personal-url"
            >
              <Copy size={12} /> Copiar
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent("Confere isso: " + myPersonalUrl)}`}
              target="_blank" rel="noopener noreferrer"
              className="px-3 py-1.5 text-xs border border-emerald-500/40 text-emerald-400 rounded-sm hover:bg-emerald-500/10"
              data-testid="share-my-personal-whatsapp"
            >
              WhatsApp
            </a>
          </div>
          <p className="text-[11px] opacity-60 mt-2">
            Formato: <code>mercadopagu.com.br/seu-nome/3primeirosCPF</code>. Compartilhe pra vender — cada venda gerada por esse link te credita comissão automaticamente.
          </p>
        </div>
      )}

      <div className="card-flat p-6 mb-10 bg-gradient-to-br from-[var(--accent)]/10 to-transparent border-[var(--accent)]/40">
        <div className="flex items-start gap-4">
          <Users size={40} color="var(--accent)" weight="duotone" />
          <div className="flex-1">
            <div className="font-display font-bold text-xl">Como funciona</div>
            <ul className="mt-2 text-sm text-white/70 space-y-1">
              <li>· Copie o link de qualquer funil abaixo — ele já vem com seu código <b className="text-[var(--accent)] font-mono">?ref={code || "..."}</b></li>
              <li>· Compartilhe no WhatsApp, redes sociais ou QR code impresso</li>
              <li>· Ao ser confirmado o pagamento, sua comissão cai automaticamente no saldo MercadoPagu</li>
              <li>· Você pode transferir ou sacar via Pix a qualquer momento</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mb-10">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="label-caps">Marketplace</div>
            <h2 className="font-display font-bold text-2xl">Funis para promover</h2>
          </div>
          <div className="label-caps text-xs">{market.total} disponíveis</div>
        </div>
        {market.funnels.length === 0 ? (
          <div className="card-flat p-10 text-center">
            <Storefront size={40} color="var(--accent)" weight="duotone" className="mx-auto" />
            <div className="mt-3 text-white/60">Nenhum funil disponível ainda no marketplace.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {market.funnels.map((f) => (
              <FunnelRow key={f.id} f={f} code={code} isAdmin={isAdmin} onDelete={deleteFunnel} />
            ))}
          </div>
        )}
        {market.pages > 1 && (
          <div className="mt-4">
            <Paginator page={market.page} pages={market.pages} onChange={loadMarket} total={market.total} />
          </div>
        )}
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="label-caps">Extrato de comissões</div>
            <h2 className="font-display font-bold text-2xl">Suas vendas por indicação</h2>
          </div>
          <div className="label-caps text-xs">{sales.total} vendas</div>
        </div>
        <div className="card-flat divide-y divide-[var(--border)]">
          {sales.sales.length === 0 ? (
            <div className="p-8 text-center text-white/50 text-sm" data-testid="no-affiliate-sales">
              Ainda não há vendas pelo seu link. Compartilhe um funil acima para começar.
            </div>
          ) : (
            sales.sales.map((s, idx) => (
              <div key={`${s.funnel_id}-${s.created_at}-${idx}`} className="p-4 flex items-center justify-between hover:bg-white/[0.02]" data-testid={`aff-sale-${idx}`}>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{s.funnel?.title || "Funil"}</div>
                  <div className="text-xs text-white/50 mt-1">
                    {s.buyer_name || s.buyer_email} · {s.plan_name} · {new Date(s.created_at).toLocaleString("pt-BR")}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-[var(--success)]">+{brl(s.affiliate_commission)}</div>
                  <div className="text-[10px] text-white/40">sobre {brl(s.amount)}</div>
                </div>
              </div>
            ))
          )}
        </div>
        {sales.pages > 1 && (
          <div className="mt-4">
            <Paginator page={sales.page} pages={sales.pages} onChange={loadSales} total={sales.total} />
          </div>
        )}
      </div>
    </div>
  );
}
